import {
  AdMob as CapAdMob,
  BannerAdSize,
  BannerAdPosition,
  BannerAdPluginEvents,
  InterstitialAdPluginEvents,
  AdmobConsentStatus,
} from '@capacitor-community/admob';

/**
 * AdManager — wraps AdMob with safe fallbacks.
 * - Banner: Home screen only, bottom, never during game
 * - Interstitial: after PlayerSelect PLAY (local & vs Computer) before board
 *
 * PRODUCTION SAFETY: native builds ALWAYS use real IDs with isTesting:false.
 * The plugin swaps real units for sample IDs when isTesting:true, so any
 * test flag on-device shows "Test ads". Capacitor WebViews run on hostname
 * "localhost", so test mode is disabled entirely when isNative() is true.
 *
 * TESTING (web dev only): add ?testAds=1 or localStorage adTestMode='1'.
 */
const REAL_IDS = {
  appId: 'ca-app-pub-5241638028763165~3771894434',
  banner: 'ca-app-pub-5241638028763165/2960928534',
  interstitial: 'ca-app-pub-5241638028763165/7724591043',
};
const GOOGLE_SAMPLE_IDS = {
  banner: 'ca-app-pub-3940256099942544/6300978111',
  interstitial: 'ca-app-pub-3940256099942544/1033173712',
};

// REAL ADS ONLY on native. Test units are web-dev only and can never
// trigger inside the Play Store build (isTesting:true makes the native
// plugin swap your real unit for Google sample IDs → "Test ads" label).
const FORCE_TEST_BUILD = false;

function isNative(): boolean {
  try {
    const cap: any = (window as any).Capacitor;
    if (cap?.isNativePlatform) {
      try { return cap.isNativePlatform() === true; } catch { return !!cap.isNativePlatform; }
    }
    if (cap?.getPlatform) return cap.getPlatform() !== 'web';
    return !!(window as any).capacitor?.isNative;
  } catch { return false; }
}

function isTestMode(): boolean {
  // BULLETPROOF: Play Store / any native build NEVER uses test ads.
  // Capacitor WebViews serve from hostname "localhost" (capacitor://localhost
  // / https://localhost), so the old `hostname === 'localhost'` check was
  // true on every real phone → isTesting:true → permanent "Test ads".
  if (isNative()) return false;
  if (FORCE_TEST_BUILD) return true;
  try {
    if (typeof window === 'undefined') return false;
    if (window.location?.search?.includes('testAds=1')) return true;
    if (window.localStorage?.getItem('adTestMode') === '1') return true;
    // vite dev on desktop browser only (native already returned false above)
    const h = window.location?.hostname ?? '';
    if (h === 'localhost' || h === '127.0.0.1') return true;
  } catch { /* ignore */ }
  return false;
}

function clearStuckTestFlag(): void {
  // Users who installed an older test build keep adTestMode='1' in WebView
  // storage across Play updates → would force test ads forever. Wipe it.
  try {
    if (window.localStorage?.getItem('adTestMode') === '1') {
      window.localStorage.removeItem('adTestMode');
      log('cleared stale adTestMode flag from old test build');
    }
  } catch { /* ignore */ }
}

function activeIds() {
  // Native always serves real units. Sample IDs are web-dev only.
  if (isNative()) return REAL_IDS;
  return isTestMode() ? GOOGLE_SAMPLE_IDS : REAL_IDS;
}

let initialized = false;
let consentHandled = false;
let canRequestAds = true;
let bannerVisible = false;
let interstitialReady = false;
let listenersAttached = false;

type LogFn = (...a: any[]) => void;
const adLog: string[] = [];
function log(...args: any[]) {
  const msg = '[AdMob] ' + args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
  adLog.push(`${new Date().toISOString()} ${msg}`);
  if (adLog.length > 200) adLog.shift();
  console.log(msg);
  try { ((window as any).__adLog = adLog.slice()); } catch {}
}
function warn(...args: any[]) {
  const msg = '[AdMob] ' + args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
  adLog.push(`${new Date().toISOString()} WARN ${msg}`);
  console.warn(msg);
  try { ((window as any).__adLog = adLog.slice()); } catch {}
}

function getAdMob(): any {
  return CapAdMob;
}

const timeout = (ms: number): Promise<void> =>
  new Promise<void>(r => setTimeout(r, ms));

function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;
  try {
    const mob = getAdMob();
    if (!mob?.addListener) return;
    mob.addListener(BannerAdPluginEvents.Loaded, () => log('banner Loaded')).catch(() => {});
    mob.addListener(BannerAdPluginEvents.FailedToLoad, (e: any) => warn('banner FailedToLoad', e)).catch(() => {});
    mob.addListener(InterstitialAdPluginEvents.Loaded, () => { interstitialReady = true; log('interstitial Loaded → ready'); }).catch(() => {});
    mob.addListener(InterstitialAdPluginEvents.FailedToLoad, (e: any) => { interstitialReady = false; warn('interstitial FailedToLoad', e); }).catch(() => {});
    // Music ducking: pause menu groove the moment the ad covers the screen,
    // resume (or stay silent for board) when it leaves. sound.ts listens for
    // these window events — AdManager stays audio-agnostic (no import cycle).
    const notifyOpen = () => { try { window.dispatchEvent(new CustomEvent('ludo:ad-open')); } catch {} };
    const notifyClose = () => { try { window.dispatchEvent(new CustomEvent('ludo:ad-close')); } catch {} };
    mob.addListener(InterstitialAdPluginEvents.Showed, () => { log('interstitial Showed → duck music'); notifyOpen(); }).catch(() => {});
    mob.addListener(InterstitialAdPluginEvents.FailedToShow, (e: any) => { warn('interstitial FailedToShow', e); notifyClose(); }).catch(() => {});
    mob.addListener(InterstitialAdPluginEvents.Dismissed, () => { log('interstitial Dismissed'); notifyClose(); }).catch(() => {});
  } catch (e) {
    warn('attachListeners failed', e);
  }
}

async function handleConsent(mob: any): Promise<boolean> {
  if (consentHandled) return canRequestAds;
  try {
    const info = await Promise.race([
      mob.requestConsentInfo(),
      timeout(4000).then(() => null),
    ]) as any;
    if (!info) { log('consent: request timed out, proceeding'); consentHandled = true; return true; }
    log('consent info', info);
    if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) {
      log('consent REQUIRED → showing form');
      try {
        const res: any = await Promise.race([mob.showConsentForm(), timeout(15000).then(() => null)]);
        if (res) {
          log('consent form result', res);
          canRequestAds = res.canRequestAds !== false;
        }
      } catch (e) {
        warn('showConsentForm failed', e);
      }
    } else {
      canRequestAds = info.canRequestAds !== false;
    }
  } catch (e) {
    warn('consent flow failed (continuing)', e);
  }
  consentHandled = true;
  try {
    ((window as any).__ads = { ...(window as any).__ads, canRequestAds, testMode: isTestMode() });
  } catch {}
  return canRequestAds;
}

async function ensureInit(): Promise<boolean> {
  if (initialized) return true;
  if (!isNative()) { log('not native → skip init (web never shows real ads)'); return false; }
  const mob = getAdMob();
  if (!mob) { warn('AdMob plugin missing'); return false; }
  attachListeners();
  try {
    // Native = production: never initialize for testing.
    const test = isNative() ? false : isTestMode();
    let done = false;
    log(`initialize (testMode=${test} native=${isNative()})`);
    await Promise.race([
      mob.initialize({
        requestTrackingAuthorization: false,
        testingDevices: [],
        initializeForTesting: test,
      }).then(() => { done = true; }),
      timeout(5000),
    ]);
    if (!done) { warn('init timed out after 5s'); return false; }
    initialized = true;
    log('initialized OK');
    const ok = await handleConsent(mob);
    if (!ok) warn('UMP says canRequestAds=false → expect no-fill until user consents');
    try {
      ((window as any).__ads = {
        initialized, canRequestAds, testMode: test,
        banner: REAL_IDS.banner, interstitial: REAL_IDS.interstitial,
        platform: (window as any).Capacitor?.getPlatform?.() ?? 'unknown',
      });
    } catch {}
    return true;
  } catch (e) {
    warn('init failed', e);
    return false;
  }
}

async function tryShowBanner(adId: string, adSize: any, isTesting: boolean): Promise<void> {
  const mob = getAdMob();
  await mob.showBanner({
    adId,
    adSize,
    position: BannerAdPosition.BOTTOM_CENTER as any,
    margin: 0,
    isTesting,
  });
}

export const AdManager = {
  /** Debug snapshot for chrome://inspect / logcat readers */
  status() {
    return {
      initialized, consentHandled, canRequestAds,
      bannerVisible, interstitialReady,
      testMode: isTestMode(), native: isNative(),
      ids: activeIds(), log: adLog.slice(-30),
    };
  },

  async init(): Promise<void> {
    try {
      (window as any).__adLog = adLog;
      clearStuckTestFlag();
      (window as any).__ads = { testMode: isTestMode(), native: isNative() };
    } catch {}
    if (await ensureInit()) {
      this.preloadInterstitial().catch(() => {});
    } else {
      warn('init() failed — banner/interstitial will no-fill. Check logcat tag "Ads".');
    }
  },

  async showHomeBanner(): Promise<boolean> {
    if (bannerVisible) {
      // Already showing — resume in case WebView paused it
      try { await getAdMob()?.resumeBanner?.(); } catch {}
      return true;
    }
    // Let home transition + WebView layout settle (fixes banner hidden)
    await new Promise(r => setTimeout(r, 500));
    // @ts-ignore re-check after delay
    if (bannerVisible) return true;
    if (!await ensureInit()) return false;
    if (!canRequestAds) { warn('showHomeBanner skipped: canRequestAds=false'); return false; }
    const mob = getAdMob();
    if (!mob) return false;
    const { banner } = activeIds();
    // Native must pass isTesting:false or the plugin swaps in sample IDs.
    const isTesting = isNative() ? false : isTestMode();
    // Attempt 1: adaptive (requested size). Attempt 2: plain BANNER fallback.
    const attempts: Array<{ size: any; label: string }> = [
      { size: BannerAdSize.ADAPTIVE_BANNER as any, label: 'ADAPTIVE_BANNER' },
      { size: BannerAdSize.BANNER as any, label: 'BANNER' },
    ];
    for (let i = 0; i < attempts.length; i++) {
      try {
        log(`showBanner try ${i + 1}/${attempts.length} ${attempts[i].label} adId=${banner} isTesting=${isTesting}`);
        await Promise.race([tryShowBanner(banner, attempts[i].size, isTesting), timeout(8000).then(() => { throw new Error('showBanner timeout 8s'); })]);
        bannerVisible = true;
        document.documentElement.style.setProperty('--ad-banner-offset', '64px');
        document.body.classList.add('has-banner');
        log('banner visible');
        return true;
      } catch (e) {
        warn(`showBanner ${attempts[i].label} failed`, e);
        try { await mob.removeBanner?.(); } catch {}
        if (i === 0) await timeout(800);
      }
    }
    document.documentElement.style.setProperty('--ad-banner-offset', '0px');
    document.body.classList.remove('has-banner');
    warn('banner: all attempts failed — likely no-fill. Verify: 1) wifi on 2) real device 3) ad units approved 4) app linked in AdMob 5) logcat "Ads" tag.');
    return false;
  },

  async hideBanner(): Promise<void> {
    const mock = document.getElementById('mock-banner');
    if (mock) mock.remove();
    if (!bannerVisible) {
      document.documentElement.style.removeProperty('--ad-banner-offset');
      document.body.classList.remove('has-banner');
      return;
    }
    const mob = getAdMob();
    if (mob) {
      try { await mob.hideBanner(); } catch (e) { warn('hideBanner failed', e); }
    }
    bannerVisible = false;
    document.documentElement.style.removeProperty('--ad-banner-offset');
    document.body.classList.remove('has-banner');
  },

  async preloadInterstitial(): Promise<void> {
    if (!await ensureInit()) return;
    if (!canRequestAds) { warn('preload skipped: canRequestAds=false'); return; }
    const mob = getAdMob();
    if (!mob) return;
    const { interstitial } = activeIds();
    const isTesting = isNative() ? false : isTestMode();
    try {
      interstitialReady = false; // stale ready flag cleared; Loaded listener sets true
      log(`prepareInterstitial adId=${interstitial} isTesting=${isTesting}`);
      await Promise.race([
        mob.prepareInterstitial({ adId: interstitial, isTesting }),
        timeout(6000).then(() => { throw new Error('prepare timeout 6s'); }),
      ]);
      // NOTE: prepare resolving ≠ loaded. Loaded listener flips ready=true.
      // Give the SDK a moment; if still not loaded, showInterstitialOnPlay retries.
      await timeout(1200);
      log(`preload settled, ready=${interstitialReady}`);
      if (!interstitialReady) {
        // One background retry — no-fill is common on sideload; don't spam
        setTimeout(() => { if (!interstitialReady) mob.prepareInterstitial({ adId: interstitial, isTesting }).catch((e: any) => warn('preload retry failed', e)); }, 8000);
      }
    } catch (e) {
      interstitialReady = false;
      warn('preload interstitial failed', e);
    }
  },

  async showInterstitialOnPlay(): Promise<boolean> {
    try {
      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        log('offline → skip interstitial');
        return false;
      }
    } catch { /* assume online */ }
    if (!await ensureInit()) return false;
    if (!canRequestAds) { warn('interstitial skipped: canRequestAds=false'); return false; }
    if (!interstitialReady) {
      log('interstitial not ready → quick preload attempt (900ms)');
      try {
        await Promise.race([this.preloadInterstitial().then(() => {}), timeout(1500)]);
      } catch {}
      // Extra grace for Loaded event to arrive
      await timeout(600);
      if (!interstitialReady) { log('interstitial still not ready → start board direct'); return false; }
    }
    const mob = getAdMob();
    if (!mob) return false;
    const notifyOpen = () => { try { window.dispatchEvent(new CustomEvent('ludo:ad-open')); } catch {} };
    const notifyClose = () => { try { window.dispatchEvent(new CustomEvent('ludo:ad-close')); } catch {} };
    try {
      if (bannerVisible) await this.hideBanner().catch(() => {});
      let dismissed = false;
      const dismissPromise = new Promise<void>(resolve => {
        let done = false;
        const finish = () => { if (done) return; done = true; dismissed = true; resolve(); };
        const t = setTimeout(finish, 15000);
        mob.addListener(InterstitialAdPluginEvents.Dismissed, () => { clearTimeout(t); finish(); }).catch(() => finish());
        mob.addListener(InterstitialAdPluginEvents.FailedToShow, (e: any) => { clearTimeout(t); warn('FailedToShow', e); finish(); }).catch(() => finish());
      });
      log('showInterstitial… (ducking music)');
      notifyOpen();
      let shown = false;
      try {
        await Promise.race([mob.showInterstitial().then(() => { shown = true; }), timeout(4000).then(() => { throw new Error('show timeout'); })]);
      } catch (e) {
        warn('showInterstitial threw (no-fill?)', e);
        shown = false;
      }
      if (!shown && !dismissed) {
        interstitialReady = false;
        notifyClose();
        this.preloadInterstitial().catch(() => {});
        return false;
      }
      await dismissPromise;
      interstitialReady = false;
      notifyClose();
      this.preloadInterstitial().catch(() => {});
      log('interstitial dismissed → start board');
      return true;
    } catch (e) {
      warn('showInterstitial failed', e);
      try { window.dispatchEvent(new CustomEvent('ludo:ad-close')); } catch {}
      return false;
    }
  },
};

export const __adDebug = { log: adLog, isTestMode, activeIds };
